/**
 * **열린 문제 보고.** 자료 읽기(`reading.ts`)가 「발견된 문제가 남아 있는 자료」(결함, 고장, 민원)로 읽은 표를 코드가 센다: 긴급도,
 * 처리 상태, 발견 뒤 지난 날수, 쏠림, 반복 항목과 메모.
 *
 * 점검 미실시 보고의 틀(경과, 「점검 완료」 조치)로 중대결함 104건을 쓴 발표자료가 「예정일 경과 102건 점검 완료」를 조치로 냈다
 * (실제 테넌트, 2026-10-27). 이 자료에서 사람이 보는 것은 급한 결함과 그 처리, 한 현장에 몰린 것, 같은 장치의 반복이다(`kb/experts.json`
 * 중대결함 분석 틀의 갈래와 같다).
 */
import type { ReportChart, TableData } from './files';
import type { ListTable } from './listTables';
import { topicParticle } from './korean';
import { shortDate } from './reportData';

/** 열린 문제 자료로 답할 때 답 단계에 주는 말. 2턴 답이 「예정일이 지난 건 102건」을 썼다(실제 테넌트, 2026-10-27). */
export const ISSUE_ANSWER = '이 자료의 행은 이미 발견된 문제다. 할 일을 안 한 것(미실시, 예정일 경과)으로 쓰지 마라.'
  + ' 아래 코드가 센 숫자(긴급도, 처리 상태, 쏠림, 반복)로 답하고, 사례를 들면 급한 것부터 든다.';

export type IssueReport = {
  /** 답과 문서 작성의 근거: 모델이 옮길 숫자. */
  facts: string[];
  /** 〈표〉 긴급도별 현황(groups, 그림 앞), 현장별 현황(top, 그림 뒤). */
  tables: ListTable[];
  charts: ReportChart[];
  /** 요약 자리의 핵심 숫자. */
  figures: { label: string; value: string }[];
  /** ※ 판정 줄: 무엇을 급함·미처리로 셌는지. */
  rule?: string;
  total: number;
  open?: number;
};

const OWNER = /담당|사원|성명|작성자|처리자|직원/;
const SITE = /현장|거래처|고객|사업장|업체|매장|지점|건물/;
/** 표와 그림에 싣는 이름 수. */
const TOP = 7;
const BARS = 6;

function text(cell: unknown): string {
  return cell === null || cell === undefined ? '' : String(cell).trim();
}

const number = (value: number) => value.toLocaleString('en-US');
const percent = (part: number, whole: number) => `${whole ? Math.round((part / whole) * 1000) / 10 : 0}%`;

function counted(rows: unknown[][], at: number): [string, number][] {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const key = text(row[at]);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return Array.from(counts.entries()).sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]));
}

/** 20261007 → 날 번호. 시각이 붙어도(「20261006 1030」) 날짜만 본다. 날짜가 아니면 0. */
function dayOf(value: string): number {
  const match = value.replace(/-/g, '').match(/^((?:19|20)\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])(?!\d)/);
  if (!match) return 0;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / 86400000;
}

/** 「A 74건(71.2%), B 5건(4.8%), … 외 N개 ○○ M건」. */
function ranking(list: [string, number][], whole: number, word: string, limit = 5): string {
  const shown = list.slice(0, limit).map(([name, count]) => `${name} ${number(count)}건(${percent(count, whole)})`);
  const rest = list.slice(limit);
  const tail = rest.length ? `, 외 ${rest.length}개 ${word} ${number(rest.reduce((sum, [, count]) => sum + count, 0))}건` : '';
  return `${shown.join(', ')}${tail}`;
}

export function issueReport(table: TableData, basis: Date): IssueReport | undefined {
  const reading = table.reading;
  if (reading?.story !== 'issues' || !table.rows.length) return undefined;
  const { columns, rows } = table;
  const at = (name: string | undefined) => (name ? columns.indexOf(name) : -1);
  const statusAt = at(reading.status?.column);
  const severityAt = at(reading.severity?.column);
  const dateAt = at(reading.date?.column);
  const unitAt = at(reading.unit);
  const groups = reading.groups.map(at).filter((index) => index >= 0);
  const site = groups.find((index) => SITE.test(columns[index] ?? '')) ?? -1;
  const owner = groups.find((index) => OWNER.test(columns[index] ?? '')) ?? -1;
  const item = groups.find((index) => index !== site && index !== owner) ?? -1;
  const total = rows.length;
  const opened = (row: unknown[]) => statusAt < 0 || (reading.status?.open ?? []).indexOf(text(row[statusAt])) >= 0;
  const open = statusAt >= 0 ? rows.filter(opened) : undefined;
  const levels = severityAt >= 0 ? (reading.severity?.order ?? []) : [];
  const levelOf = (row: unknown[]) => text(row[severityAt]);
  const facts: string[] = [];
  const statusName = columns[statusAt] ?? '';
  const openWord = '미처리';

  // 긴급도와 처리 상태.
  const bySeverity = levels.map((level) => {
    const mine = rows.filter((row) => levelOf(row) === level);
    return { level, count: mine.length, open: mine.filter(opened).length };
  }).filter((one) => one.count > 0);
  const others = severityAt >= 0 ? total - bySeverity.reduce((sum, one) => sum + one.count, 0) : 0;
  if (bySeverity.length) {
    facts.push(`전체 ${number(total)}건 중 ${bySeverity.map((one) => `${one.level} ${number(one.count)}건(${percent(one.count, total)})`).join(', ')}`
      + `${others > 0 ? `, 그 밖 ${number(others)}건` : ''}`);
  } else {
    facts.push(`전체 ${number(total)}건`);
  }
  if (open) {
    facts.push(open.length === total ? `모두 ${openWord}(${number(total)}건)`
      : `${openWord} ${number(open.length)}건(${percent(open.length, total)}), 처리된 것 ${number(total - open.length)}건`);
    if (bySeverity.length > 1) {
      facts.push(`${openWord} 중 ${bySeverity.map((one) => `${one.level} ${number(one.open)}건`).join(', ')}`);
    }
  }

  // 발견 뒤 지난 날수(남은 문제만).
  const today = Date.UTC(basis.getFullYear(), basis.getMonth(), basis.getDate()) / 86400000;
  if (dateAt >= 0 && reading.date?.role === 'found') {
    const waiting = (open ?? rows).map((row) => ({ row, day: dayOf(text(row[dateAt])) })).filter((one) => one.day > 0 && one.day <= today);
    if (waiting.length) {
      const ages = waiting.map((one) => today - one.day);
      const oldest = waiting.reduce((least, one) => (one.day < least.day ? one : least));
      const over14 = ages.filter((age) => age > 14).length;
      const mid = ages.filter((age) => age >= 7 && age <= 14).length;
      const fresh = ages.filter((age) => age < 7).length;
      const whose = open ? openWord : '전체';
      facts.push(`${whose} ${number(waiting.length)}건의 ${columns[dateAt]}부터 기준일까지 지난 날수: 14일 넘음 ${number(over14)}건, `
        + `7~14일 ${number(mid)}건, 7일 미만 ${number(fresh)}건, 가장 오래된 건 ${shortDate(text(oldest.row[dateAt]))}(${today - oldest.day}일)`);
    }
  }

  // 쏠림: 묶음 칸마다 상위.
  groups.forEach((index) => {
    const name = columns[index] ?? '';
    facts.push(`${name}별: ${ranking(counted(rows, index), total, name)}`);
  });
  const sites = site >= 0 ? counted(rows, site) : [];
  const [topSite] = sites;
  if (topSite && total >= 10 && topSite[1] / total >= 0.3) {
    const mine = rows.filter((row) => text(row[site]) === topSite[0]);
    const units = unitAt >= 0 ? new Set(mine.map((row) => text(row[unitAt])).filter(Boolean)).size : 0;
    const items = item >= 0 ? counted(mine, item).slice(0, 3).map(([name, count]) => `${name} ${number(count)}건`).join(', ') : '';
    const days = dateAt >= 0 ? new Set(mine.map((row) => text(row[dateAt])).filter(Boolean)).size : 0;
    facts.push(`쏠림: ${topSite[0]} ${number(topSite[1])}건(전체의 ${percent(topSite[1], total)})`
      + `${units ? `, 호기 ${number(units)}개` : ''}${days ? `, ${columns[dateAt]} ${number(days)}일` : ''}${items ? `, 많은 항목 ${items}` : ''}`);
  }

  // 가장 급한 등급이 어디에 나왔는지.
  const [worst] = bySeverity;
  if (worst && bySeverity.length > 1) {
    const mine = rows.filter((row) => levelOf(row) === worst.level);
    const where = site >= 0 ? ranking(counted(mine, site), mine.length, columns[site] ?? '', 3) : '';
    const what = item >= 0 ? ranking(counted(mine, item), mine.length, columns[item] ?? '', 3) : '';
    facts.push(`${worst.level} ${number(worst.count)}건의 ${[where && `${columns[site]}: ${where}`, what && `${columns[item]}: ${what}`].filter(Boolean).join(' / ')}`);
  }

  // 같은 메모가 여러 번 나온 것.
  reading.notes.map(at).filter((index) => index >= 0).forEach((index) => {
    const repeated = counted(rows, index).filter(([, count]) => count >= 2).slice(0, 3);
    if (repeated.length) {
      facts.push(`${columns[index]} 반복: ${repeated.map(([note, count]) => `「${note.length > 60 ? `${note.slice(0, 60)}…` : note}」 ${number(count)}건`).join(', ')}`);
    }
  });
  if (open && owner >= 0) {
    facts.push(`담당별 ${openWord}: ${ranking(counted(open, owner), open.length, columns[owner] ?? '', 3)}`);
  }

  // 표: 긴급도별 현황(끝 줄 「계」), 현장별 현황.
  const tables: ListTable[] = [];
  if (bySeverity.length) {
    const head = ['구분', '건수', ...(open ? [openWord] : [])];
    const lines = [...bySeverity.map((one) => [one.level, one.count, ...(open ? [one.open] : [])]),
      ...(others > 0 ? [['그 밖', others, ...(open ? [rows.filter((row) => levels.indexOf(levelOf(row)) < 0 && opened(row)).length] : [])]] : []),
      ['계', total, ...(open ? [open.length] : [])]];
    tables.push({ role: 'groups', caption: `${columns[severityAt]}별 현황 (단위: 건)`, table: { columns: head, rows: lines } });
  }
  if (site >= 0 && sites.length > 1) {
    const shownLevels = bySeverity.slice(0, 3).map((one) => one.level);
    // 발표자료 표는 여섯 칸까지다(slides.ts). 넘치면 담당 칸을 뺀다(독립 검토: 마지막 칸이 잘렸다).
    const withOwner = owner >= 0 && 2 + shownLevels.length + (item >= 0 ? 1 : 0) + 1 <= 6;
    const head = [columns[site] ?? '현장', '건수', ...shownLevels, ...(item >= 0 ? [`많은 ${columns[item]}`] : []),
      ...(withOwner ? [columns[owner] ?? '담당'] : [])];
    const lineOf = (name: string, mine: unknown[][]) => [name, mine.length, ...shownLevels.map((level) => mine.filter((row) => levelOf(row) === level).length),
      ...(item >= 0 ? [counted(mine, item)[0]?.[0] ?? ''] : []),
      ...(withOwner ? [counted(mine, owner).slice(0, 2).map(([name]) => name).join(', ')] : [])];
    const shown = sites.slice(0, TOP);
    const rest = sites.slice(TOP);
    const restRows = rows.filter((row) => rest.some(([name]) => name === text(row[site])));
    const lines = [...shown.map(([name]) => lineOf(name, rows.filter((row) => text(row[site]) === name))),
      ...(rest.length ? [[`그 밖 ${rest.length}개 ${columns[site]}`, restRows.length,
        ...shownLevels.map((level) => restRows.filter((row) => levelOf(row) === level).length),
        ...(item >= 0 ? [''] : []), ...(withOwner ? [''] : [])]] : [])];
    tables.push({ role: 'top', caption: `${columns[site]}별 현황 (상위 ${shown.length}, 단위: 건)`, table: { columns: head, rows: lines } });
  }

  // 그림: 항목별, 현장별 상위. 긴급도가 둘 이상이면 긴급도로 쌓는다.
  const chartOf = (index: number): ReportChart | undefined => {
    const list = counted(rows, index).slice(0, BARS);
    if (list.length < 2) return undefined;
    const categories = list.map(([name]) => name);
    // 긴급도 밖 값(그 밖)도 계열로 둔다. 빼면 막대 높이가 범주 건수보다 낮다(독립 검토).
    const series = bySeverity.length > 1
      ? [...bySeverity.map((one) => ({ name: one.level, values: categories.map((name) => rows.filter((row) => text(row[index]) === name && levelOf(row) === one.level).length) })),
        ...(others > 0 ? [{ name: '그 밖', values: categories.map((name) => rows.filter((row) => text(row[index]) === name && levels.indexOf(levelOf(row)) < 0).length) }] : [])]
      : [{ name: '건수', values: list.map(([, count]) => count) }];
    return {
      ...(bySeverity.length > 1 ? { kind: 'stack' as const } : {}),
      title: `${columns[index]}별 건수(상위 ${categories.length})`,
      categories, series, unit: '건',
      ...(bySeverity.length > 1 && worst ? { highlight: worst.level } : {}),
    };
  };
  const charts = [item, site].filter((index) => index >= 0).map(chartOf).filter((chart): chart is ReportChart => !!chart);

  const figures = [{ label: '전체', value: `${number(total)}건` },
    ...(worst && bySeverity.length > 1 ? [{ label: worst.level, value: `${number(worst.count)}건` }] : []),
    ...(open ? [{ label: openWord, value: `${number(open.length)}건` }] : [])];
  const openValues = (reading.status?.open ?? []).map((value) => (value ? `「${value}」` : '빈 칸'));
  const rule = [levels.length ? `${topicParticle(columns[severityAt] ?? '')} ${bySeverity.map((one) => one.level).join(', ')} 차례로 급함` : '',
    open ? `${openWord}는 ${statusName} ${openValues.join(', ')}인 건` : ''].filter(Boolean).join(', ');
  return {
    facts, tables, charts, figures, total,
    ...(rule ? { rule } : {}),
    ...(open ? { open: open.length } : {}),
  };
}
